import React, { useState } from 'react';
import { Star, Trash2, MessageSquare, ExternalLink, Copy, Check } from 'lucide-react';

const Stars = ({ rating }) => (
  <div className="flex space-x-0.5">
    {[1, 2, 3, 4, 5].map(n => (
      <Star key={n} className={`w-4 h-4 ${rating >= n ? 'fill-amber-400 text-amber-400' : 'text-border'}`} />
    ))}
  </div>
);

const ReviewsAdminTab = ({ reviews, password, onRefresh }) => {
  const [busy, setBusy] = useState(null);
  const [copied, setCopied] = useState(false);

  const reviewLink = 'https://rickysbees.com.au/review';

  const copyLink = () => {
    navigator.clipboard.writeText(reviewLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const setStatus = async (review, status) => {
    setBusy(review.review_id);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/${review.review_id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, status })
      });
      onRefresh();
    } finally {
      setBusy(null);
    }
  };

  const deleteReview = async (review) => {
    if (!window.confirm(`Delete review from ${review.name}? This cannot be undone.`)) return;
    setBusy(review.review_id);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/${review.review_id}?password=${encodeURIComponent(password)}`, { method: 'DELETE' });
      onRefresh();
    } finally {
      setBusy(null);
    }
  };

  const pending = reviews.filter(r => r.status === 'pending').length;

  return (
    <div data-testid="reviews-admin-tab">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>Reviews</h1>
          {pending > 0 && (
            <p className="text-sm text-amber-600 mt-1 font-medium" data-testid="pending-reviews-count">{pending} awaiting approval</p>
          )}
        </div>
        <div className="flex items-center space-x-2 p-3 rounded-xl border border-border bg-muted/30">
          <span className="text-xs text-foreground/60">Review link to send customers:</span>
          <code className="text-xs font-medium text-primary">{reviewLink}</code>
          <button onClick={copyLink} className="p-1.5 rounded-lg hover:bg-muted transition-colors" data-testid="copy-review-link">
            {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-foreground/60" />}
          </button>
          <a href="/review" target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-lg hover:bg-muted transition-colors">
            <ExternalLink className="w-4 h-4 text-foreground/60" />
          </a>
        </div>
      </div>

      {reviews.length === 0 ? (
        <div className="text-center py-16">
          <MessageSquare className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <p className="text-lg text-foreground/70">No reviews yet</p>
          <p className="text-sm text-foreground/50 mt-1">Send customers your review link to start collecting</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map(review => (
            <div key={review.review_id} className={`p-5 rounded-2xl border bg-background/50 ${review.status === 'pending' ? 'border-amber-300' : 'border-border'}`} data-testid={`review-${review.review_id}`}>
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center flex-wrap gap-3 mb-2">
                    <Stars rating={review.rating} />
                    <span className="font-semibold text-foreground text-sm">{review.name}</span>
                    {(review.suburb || review.state) && (
                      <span className="text-xs text-foreground/50">{[review.suburb, review.state].filter(Boolean).join(', ')}</span>
                    )}
                    {review.product && <span className="px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary">{review.product}</span>}
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${review.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`} data-testid={`review-status-${review.review_id}`}>
                      {review.status === 'approved' ? 'Published' : 'Pending'}
                    </span>
                  </div>
                  <p className="text-sm text-foreground/80">"{review.text}"</p>
                  <p className="text-xs text-foreground/40 mt-2">
                    {new Date(review.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                </div>
                <div className="flex items-center space-x-2 flex-shrink-0">
                  {review.status === 'pending' ? (
                    <button onClick={() => setStatus(review, 'approved')} disabled={busy === review.review_id}
                      className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
                      data-testid={`approve-review-${review.review_id}`}>
                      {busy === review.review_id ? '...' : 'Approve & Publish'}
                    </button>
                  ) : (
                    <button onClick={() => setStatus(review, 'pending')} disabled={busy === review.review_id}
                      className="px-4 py-2 rounded-lg border border-amber-300 text-amber-700 text-xs font-medium hover:bg-amber-50 transition-colors disabled:opacity-50"
                      data-testid={`unpublish-review-${review.review_id}`}>
                      {busy === review.review_id ? '...' : 'Unpublish'}
                    </button>
                  )}
                  <button onClick={() => deleteReview(review)} disabled={busy === review.review_id}
                    className="p-2 text-red-400 hover:text-red-600 transition-colors disabled:opacity-50" data-testid={`delete-review-${review.review_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewsAdminTab;
